import * as THREE from "three";
import type { Scenery } from "../../prims/scenery";
import type { Surface } from "../styles";
import { toPoints } from "./coaster";
import type { Kit } from "./kit";

// The indoor detail kit (#181): what hangs off the walls and the roof of a
// warehouse-style map. Steel trusses and roof beams, a catwalk, tall factory
// windows, hanging lamps, pipe runs and ducts, a painted mural, pallets and
// crates; and (#246, The Depot) a glass control booth high on a wall, sheets
// of plywood scattered on the floor and shafts of light from the windows.
// Drawn only, like all scenery. The wall pieces (window, mural, booth,
// shaft) face +u, standing in the plane across it; the long ones (truss,
// beam, catwalk) run `len` along w, like a building's front.

const STEEL = "#5b6470";
const GALVANISED = "#aeb5bb";
const PINE = "#c49a63";

/** A box from `a` to `b`, `w` wide (level, sideways) and `h` tall, for a run that needn't be square to the axes. */
function strut(kit: Kit, parent: THREE.Object3D, a: THREE.Vector3, b: THREE.Vector3, w: number, h: number, color: THREE.ColorRepresentation, surface: Surface): THREE.Mesh {
  const m = kit.box(parent, w, h, a.distanceTo(b), color, 0, 0, 0, surface);
  m.position.copy(a).add(b).multiplyScalar(0.5);
  m.lookAt(b);
  return m;
}

/** A steel roof truss spanning `len` (12 m) along w, its bottom chord `h` (6 m) up: two chords, posts and diagonals. */
export function truss(kit: Kit, s: Scenery): THREE.Object3D {
  const root = new THREE.Group();
  const len = s.len ?? 12;
  const y = s.h ?? 6;
  const deep = 1.1;
  const color = s.color ?? STEEL;
  for (const top of [0, deep]) kit.box(root, 0.18, 0.18, len, color, 0, y + top - 0.09, 0, "paintedMetal");
  const bays = Math.max(2, Math.round(len / 1.5));
  for (let i = 0; i <= bays; i++) {
    const z = -len / 2 + (i * len) / bays;
    kit.rod(root, new THREE.Vector3(0, y, z), new THREE.Vector3(0, y + deep, z), 0.045, color, "paintedMetal", 6);
    if (i === bays) break;
    // The diagonals lean toward the middle from both ends, like a Pratt truss.
    const z2 = -len / 2 + ((i + 1) * len) / bays;
    const [lo, hi] = i < bays / 2 ? [z2, z] : [z, z2];
    kit.rod(root, new THREE.Vector3(0, y, lo), new THREE.Vector3(0, y + deep, hi), 0.04, color, "paintedMetal", 6);
  }
  return root;
}

/** A plain I-beam `len` (12 m) along w, its underside `h` (6 m) up. */
export function beam(kit: Kit, s: Scenery): THREE.Object3D {
  const root = new THREE.Group();
  const len = s.len ?? 12;
  const y = s.h ?? 6;
  const color = s.color ?? STEEL;
  kit.box(root, 0.3, 0.04, len, color, 0, y, 0, "paintedMetal");
  kit.box(root, 0.03, 0.42, len, color, 0, y + 0.04, 0, "paintedMetal");
  kit.box(root, 0.3, 0.04, len, color, 0, y + 0.46, 0, "paintedMetal");
  return root;
}

/** A catwalk `len` (10 m) along w and `wide` (1.2 m) across, its deck `h` (4 m) up: grating, railings both sides, legs to the floor. */
export function catwalk(kit: Kit, s: Scenery): THREE.Object3D {
  const root = new THREE.Group();
  const len = s.len ?? 10;
  const wide = s.wide ?? 1.2;
  const y = s.h ?? 4;
  const color = s.color ?? "#d9a21b";
  kit.box(root, wide, 0.06, len, "#3a3f45", 0, y - 0.06, 0, "metal");
  for (const side of [-1, 1]) {
    const x = (side * wide) / 2;
    kit.box(root, 0.02, 0.12, len, color, x, y - 0.06, 0, "paintedMetal");
    const posts = Math.max(1, Math.round(len / 1.5));
    for (let i = 0; i <= posts; i++) {
      const z = -len / 2 + (i * len) / posts;
      kit.box(root, 0.05, 1.05, 0.05, color, x, y, z, "paintedMetal");
    }
    for (const rail of [0.5, 1.05]) kit.rod(root, new THREE.Vector3(x, y + rail, -len / 2), new THREE.Vector3(x, y + rail, len / 2), 0.025, color, "paintedMetal", 6);
  }
  const legs = Math.max(1, Math.round(len / 6));
  for (let i = 0; i <= legs; i++) {
    const z = -len / 2 + 0.2 + (i * (len - 0.4)) / legs;
    for (const side of [-1, 1]) kit.box(root, 0.12, y - 0.06, 0.12, STEEL, (side * (wide - 0.2)) / 2, 0, z, "paintedMetal");
  }
  return root;
}

/** Glass that reads lit from outside whatever the light inside: an unlit pane. */
function pane(kit: Kit, parent: THREE.Object3D, w: number, h: number, color: THREE.ColorRepresentation, x: number, y: number, z: number): void {
  const m = new THREE.Mesh(kit.geom("box", () => new THREE.BoxGeometry(1, 1, 1)), kit.glow(color));
  m.scale.set(0.02, h, w);
  m.position.set(x, y + h / 2, z);
  parent.add(m);
}

/** A tall steel factory window, `len` (4 m) across and `h` (5 m) tall from a sill 1.2 m up, small panes in a grid, facing +u. */
export function factoryWindow(kit: Kit, s: Scenery): THREE.Object3D {
  const root = new THREE.Group();
  const len = s.len ?? 4;
  const h = s.h ?? 5;
  const sill = 1.2;
  const color = s.color ?? "#2c3238";
  const cols = Math.max(2, Math.round(len / 0.6));
  const rows = Math.max(2, Math.round(h / 0.5));
  // Daylight through old glass: most panes pale, a few dimmer (painted over or grimy).
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const dim = (r * 7 + c * 3 + Math.abs(Math.round(s.x + s.z))) % 11 === 0;
      pane(kit, root, len / cols, h / rows, dim ? "#9fb0b8" : "#e9f1f2", 0.02, sill + (r * h) / rows, -len / 2 + ((c + 0.5) * len) / cols);
    }
  }
  for (let c = 0; c <= cols; c++) kit.box(root, 0.06, h, 0.04, color, 0.05, sill, -len / 2 + (c * len) / cols, "paintedMetal");
  for (let r = 0; r <= rows; r++) kit.box(root, 0.06, 0.04, len, color, 0.05, sill + (r * h) / rows - 0.02, 0, "paintedMetal");
  kit.box(root, 0.3, 0.08, len + 0.2, "#8d8a84", 0.12, sill - 0.08, 0, "concrete");
  return root;
}

/** A hanging work lamp: a cord down from the roof `h` (7 m) up, `len` (2.5 m) long, a wide enamel shade and a bulb. */
export function pendant(kit: Kit, s: Scenery): THREE.Object3D {
  const root = new THREE.Group();
  const top = s.h ?? 7;
  const y = top - (s.len ?? 2.5);
  kit.rod(root, new THREE.Vector3(0, top, 0), new THREE.Vector3(0, y + 0.3, 0), 0.01, "#1d1f22", "plastic", 3);
  const shade = new THREE.Mesh(
    kit.geom("lampshade", () => new THREE.CylinderGeometry(0.08, 0.38, 0.3, 14, 1, true)),
    kit.mat(s.color ?? "#2f5d50", "paintedMetal", { side: THREE.DoubleSide }),
  );
  shade.position.set(0, y + 0.15, 0);
  root.add(shade);
  const bulb = new THREE.Mesh(kit.geom("lampbulb", () => new THREE.IcosahedronGeometry(0.1, 1)), kit.glow("#fff1c9"));
  bulb.position.set(0, y + 0.06, 0);
  root.add(bulb);
  return root;
}

/** A path's points, each at its own `y` or `fallback` metres up when 0. */
const pathAt = (s: Scenery, fallback: number) => toPoints(s.path!).map((p) => p.setY(p.y || fallback));

/** Level sideways of the run from `a` to `b`. */
const across = (a: THREE.Vector3, b: THREE.Vector3) => new THREE.Vector3(b.z - a.z, 0, a.x - b.x).normalize();

/**
 * Two pipes side by side along a path, 5 m up where a point's `y` is 0: a
 * red sprinkler main and a grey one (`color`), `wide` (0.24 m) across each,
 * an elbow at every corner and a hanger every 3 m.
 */
export function pipes(kit: Kit, s: Scenery): THREE.Object3D {
  const root = new THREE.Group();
  const r = (s.wide ?? 0.24) / 2;
  const pts = pathAt(s, 5);
  const colors = ["#b0322a", s.color ?? "#7d8790"];
  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i]!;
    const b = pts[i + 1]!;
    const side = across(a, b).multiplyScalar(r * 1.6);
    colors.forEach((color, k) => {
      const off = side.clone().multiplyScalar(k === 0 ? 1 : -1);
      kit.rod(root, a.clone().add(off), b.clone().add(off), r, color, "paintedMetal", 10);
      kit.ball(root, r * 1.15, color, b.x + off.x, b.y, b.z + off.z, "paintedMetal");
      if (i === 0) kit.ball(root, r * 1.15, color, a.x + off.x, a.y, a.z + off.z, "paintedMetal");
    });
    const n = Math.max(1, Math.round(a.distanceTo(b) / 3));
    for (let j = 0; j < n; j++) {
      const p = a.clone().lerp(b, (j + 0.5) / n);
      kit.rod(root, p.clone().setY(p.y + r), p.clone().setY(p.y + r + 0.6), 0.015, STEEL, "metal", 4);
      strut(kit, root, p.clone().addScaledVector(side, 2.2).setY(p.y - r), p.clone().addScaledVector(side, -2.2).setY(p.y - r), 0.04, 0.04, STEEL, "metal");
    }
  }
  return root;
}

/** A rectangular sheet-metal duct along a path, 6 m up where a point's `y` is 0, `wide` (0.9 m) across, a flanged joint every 1.5 m. */
export function duct(kit: Kit, s: Scenery): THREE.Object3D {
  const root = new THREE.Group();
  const w = s.wide ?? 0.9;
  const h = w * 0.65;
  const color = s.color ?? GALVANISED;
  const pts = pathAt(s, 6);
  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i]!;
    const b = pts[i + 1]!;
    strut(kit, root, a, b, w, h, color, "metal").position.y -= h / 2;
    const n = Math.max(1, Math.round(a.distanceTo(b) / 1.5));
    for (let j = 1; j < n; j++) {
      const p = a.clone().lerp(b, j / n);
      const dir = b.clone().sub(a).normalize().multiplyScalar(0.03);
      strut(kit, root, p.clone().sub(dir), p.clone().add(dir), w + 0.06, h + 0.06, color, "metal").position.y -= h / 2;
    }
    // A corner gets a box the duct's size, so the turn is closed.
    if (i < pts.length - 2) kit.box(root, w, h, w, color, b.x, b.y - h, b.z, "metal");
  }
  return root;
}

/** Spray-can colours, picked per mural from where it stands. */
const SPRAY = ["#ef476f", "#ffd166", "#06d6a0", "#4cc9f0", "#9b5de5", "#f78c6b"];

/**
 * A painted mural (graffiti) on a wall, facing +u: `len` (4 m) across and
 * `h` (1.6 m) tall, bottom 0.4 m up. A throw-up: fat round letters leaning
 * into each other over one dark outline, a counter punched in each, a drip
 * or two and a white shine, on a cloud of a second colour. Its colours and
 * shapes come from where it stands, so no two murals match.
 */
export function mural(kit: Kit, s: Scenery): THREE.Object3D {
  const root = new THREE.Group();
  const len = s.len ?? 4;
  const h = s.h ?? 1.6;
  let seed = Math.abs(Math.round(s.x * 31 + s.z * 17)) + 1;
  const rand = () => ((seed = (seed * 16807) % 2147483647) - 1) / 2147483646;
  const fill = s.color ?? SPRAY[seed % SPRAY.length]!;
  const back = SPRAY[(seed + 3) % SPRAY.length]!;
  const disc = kit.geom("muraldisc", () => new THREE.CircleGeometry(1, 20));
  // Each layer a little further off the wall than the one under it.
  const blob = (color: THREE.ColorRepresentation, layer: number, y: number, z: number, rw: number, rh: number, tilt = 0) => {
    const m = new THREE.Mesh(disc, kit.mat(color, "paint", { roughness: 0.6 }));
    m.rotation.set(0, Math.PI / 2, tilt);
    m.scale.set(rw, rh, 1);
    m.position.set(0.004 + layer * 0.004, 0.4 + y, z);
    root.add(m);
  };
  for (let i = 0; i < 5; i++) blob(back, 0, h * (0.35 + rand() * 0.3), -len / 2 + len * (0.12 + rand() * 0.76), len * 0.16, h * 0.42);
  const letters = Math.max(2, Math.round(len / 0.8));
  const step = (len * 0.9) / letters;
  const shapes = Array.from({ length: letters }, (_, i) => ({
    z: -len * 0.45 + (i + 0.5) * step,
    y: h * (0.48 + (rand() - 0.5) * 0.12),
    rw: step * (0.6 + rand() * 0.12),
    rh: h * (0.3 + rand() * 0.07),
    tilt: (rand() - 0.5) * 0.5,
  }));
  for (const l of shapes) blob("#1b1b22", 1, l.y, l.z, l.rw + 0.06, l.rh + 0.06, l.tilt);
  for (const l of shapes) blob(fill, 2, l.y, l.z, l.rw, l.rh, l.tilt);
  for (const l of shapes) {
    blob("#1b1b22", 3, l.y + (rand() - 0.5) * l.rh * 0.3, l.z + (rand() - 0.5) * l.rw * 0.3, l.rw * 0.12, l.rh * 0.3, l.tilt);
    blob("#ffffff", 3, l.y + l.rh * 0.55, l.z - l.rw * 0.35, l.rw * 0.14, l.rh * 0.1, 0.5);
    if (rand() < 0.5) blob(fill, 2, l.y - l.rh - h * 0.06, l.z + (rand() - 0.5) * l.rw, 0.025, h * 0.1);
  }
  return root;
}

/** One pallet, 1.2 m along u and 1.0 m across, 0.15 m tall from `y`: two bottom boards, three stringers, five deck boards. */
function onePallet(kit: Kit, root: THREE.Object3D, y: number, wood: THREE.ColorRepresentation): void {
  for (const x of [-0.53, 0.53]) kit.box(root, 0.14, 0.03, 1.0, wood, x, y, 0, "plywood");
  for (const z of [-0.45, 0, 0.45]) kit.box(root, 1.2, 0.09, 0.1, wood, 0, y + 0.03, z, "plywood");
  for (let i = 0; i < 5; i++) kit.box(root, 0.14, 0.03, 1.0, wood, -0.53 + i * 0.265, y + 0.12, 0, "plywood");
}

/** A stack of wooden pallets about `h` (0.15 m, one pallet) tall. */
export function pallet(kit: Kit, s: Scenery): THREE.Object3D {
  const root = new THREE.Group();
  const n = Math.max(1, Math.round((s.h ?? 0.15) / 0.15));
  const wood = s.color ?? PINE;
  for (let i = 0; i < n; i++) onePallet(kit, root, i * 0.15, wood);
  return root;
}

/** A wooden shipping crate `h` (1 m) on a side: plywood box, battens round its edges and a brace across each face. */
export function crate(kit: Kit, s: Scenery): THREE.Object3D {
  const root = new THREE.Group();
  const d = s.h ?? 1;
  const wood = s.color ?? PINE;
  const batten = "#8c6239";
  kit.box(root, d, d, d, wood, 0, 0, 0, "plywood");
  const t = 0.07;
  for (const x of [-1, 1]) {
    for (const z of [-1, 1]) kit.box(root, t, d, t, batten, (x * (d - t)) / 2 + x * 0.01, 0, (z * (d - t)) / 2 + z * 0.01, "plywood");
  }
  for (const y of [0, d - t]) {
    for (const x of [-1, 1]) kit.box(root, t, t, d, batten, (x * (d - t)) / 2 + x * 0.015, y, 0, "plywood");
    for (const z of [-1, 1]) kit.box(root, d, t, t, batten, 0, y, (z * (d - t)) / 2 + z * 0.015, "plywood");
  }
  // A diagonal brace on each side face, corner to corner.
  for (const x of [-1, 1]) {
    const fx = (x * d) / 2 + x * 0.02;
    strut(kit, root, new THREE.Vector3(fx, t, -d / 2 + t), new THREE.Vector3(fx, d - t, d / 2 - t), 0.02, t, batten, "plywood");
  }
  return root;
}

/** The control booth's name board: a made-up yard office, never a real brand. */
const BOOTH_SIGN = { name: "YARD OFFICE", line: "STAFF ONLY" };

/**
 * A glass control booth (#246) jutting out of a wall, its floor `h` (6 m) up
 * and `len` (4 m) across: a steel floor on two braces back to the wall, a
 * panel to waist height, lit glass round three sides, a flat roof, a desk
 * and a screen glowing inside, and the yard office's sign under it.
 */
export function booth(kit: Kit, s: Scenery): THREE.Object3D {
  const root = new THREE.Group();
  const y = s.h ?? 6;
  const len = s.len ?? 4;
  const deep = 2.2;
  const steel = s.color ?? "#4b5560";
  const lit = "#f3dcaa";
  kit.box(root, deep, 0.25, len, steel, deep / 2, y - 0.25, 0, "paintedMetal");
  kit.box(root, deep + 0.15, 0.18, len + 0.15, steel, deep / 2, y + 2.5, 0, "paintedMetal");
  for (const z of [-1, 1]) strut(kit, root, new THREE.Vector3(0.05, y - 2.2, (z * len) / 2.6), new THREE.Vector3(deep - 0.2, y - 0.25, (z * len) / 2.6), 0.14, 0.14, steel, "paintedMetal");
  // Waist-high steel panels, then glass to the roof, lit from inside.
  kit.box(root, 0.06, 0.95, len, steel, deep, y, 0, "paintedMetal");
  pane(kit, root, len, 1.5, lit, deep, y + 0.95, 0);
  for (const z of [-1, 1]) {
    kit.box(root, deep, 0.95, 0.06, steel, deep / 2, y, (z * len) / 2, "paintedMetal");
    const side = new THREE.Mesh(kit.geom("box", () => new THREE.BoxGeometry(1, 1, 1)), kit.glow(lit));
    side.scale.set(deep, 1.5, 0.02);
    side.position.set(deep / 2, y + 0.95 + 0.75, (z * len) / 2);
    root.add(side);
  }
  // Mullions: the corners and the middle of the front.
  for (const z of [-len / 2, 0, len / 2]) kit.box(root, 0.08, 1.55, 0.08, steel, deep, y + 0.95, z, "paintedMetal");
  kit.box(root, 0.08, 1.55, 0.08, steel, 0.04, y + 0.95, -len / 2, "paintedMetal");
  kit.box(root, 0.08, 1.55, 0.08, steel, 0.04, y + 0.95, len / 2, "paintedMetal");
  // Inside: a desk along the glass and a screen on it.
  kit.box(root, 0.6, 0.75, len * 0.7, PINE, deep - 0.45, y, 0, "plywood");
  const screen = new THREE.Mesh(kit.geom("box", () => new THREE.BoxGeometry(1, 1, 1)), kit.glow("#8fd0c4"));
  screen.scale.set(0.05, 0.35, 0.55);
  screen.position.set(deep - 0.6, y + 0.95, len * 0.15);
  root.add(screen);
  kit.sign(root, BOOTH_SIGN, "#c8402e", Math.min(len * 0.7, 2.8), deep + 0.02, y - 0.75, 0);
  return root;
}

/** One sheet of plywood, 2.4 x 1.2 m and 18 mm thick, lying at `y` turned `yaw`. */
function sheet(kit: Kit, root: THREE.Object3D, x: number, z: number, y: number, yaw: number, wood: THREE.ColorRepresentation): void {
  const m = kit.box(root, 2.4, 0.018, 1.2, wood, x, y, z, "plywood");
  m.rotation.y = yaw;
}

/**
 * Sheets of plywood (#246) dropped in a loose pile `len` (2.5 m) across on
 * the floor, a few overlapping: lying flat, so the board rolls over them.
 * Where it stands picks how they lie.
 */
export function plywood(kit: Kit, s: Scenery): THREE.Object3D {
  const root = new THREE.Group();
  const spread = (s.len ?? 2.5) / 2;
  const woods = [s.color ?? PINE, "#b3874f", "#d2ad74"];
  let seed = Math.abs(Math.round(s.x * 7 + s.z * 13)) + 1;
  const rand = () => ((seed = (seed * 16807) % 2147483647) / 2147483647);
  const n = Math.max(3, Math.round(spread * 2.4));
  for (let i = 0; i < n; i++) {
    const a = rand() * Math.PI * 2;
    const r = spread * Math.sqrt(rand());
    sheet(kit, root, Math.cos(a) * r, Math.sin(a) * r, 0.002 + i * 0.019, rand() * Math.PI, woods[i % woods.length]!);
  }
  return root;
}

/**
 * A shaft of daylight (#246) from a high window down to the floor: it
 * leaves the wall `h` (7 m) up, `wide` (2.5 m) across, and slants down to
 * land `len` (6 m) out along u. Unlit and see-through, adding to what's
 * behind it, so it reads as dusty light in a dark room.
 */
export function shaft(kit: Kit, s: Scenery): THREE.Object3D {
  const root = new THREE.Group();
  const top = s.h ?? 7;
  const out = s.len ?? 6;
  const half = (s.wide ?? 2.5) / 2;
  // A slanted slab: the window's opening at the wall, a wider patch on the floor.
  const g = new THREE.BufferGeometry();
  const spread = 1.35;
  const p = [
    [0, top, -half],
    [0, top, half],
    [0, top - 1.6, half],
    [0, top - 1.6, -half],
    [out - 1.2, 0.02, -half * spread],
    [out - 1.2, 0.02, half * spread],
    [out + 1.2, 0.02, half * spread],
    [out + 1.2, 0.02, -half * spread],
  ];
  const faces = [
    [0, 1, 5, 0, 5, 4],
    [3, 2, 6, 3, 6, 7],
    [0, 4, 7, 0, 7, 3],
    [1, 2, 6, 1, 6, 5],
  ].flat();
  g.setAttribute("position", new THREE.Float32BufferAttribute(faces.flatMap((i) => p[i]!), 3));
  g.computeVertexNormals();
  root.add(new THREE.Mesh(g, kit.haze(s.color ?? "#ffe9b8", 0.09)));
  return root;
}
